import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Cube, MagnifyingGlassPlus, X } from "@phosphor-icons/react";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { EdgeSection } from "@/components/site/EdgeSection";
import { Edge3DInline, Edge3DModal, useEdgeStone } from "@/components/site/Edge3DViewer";
import { useEdgeStones } from "@/components/site/StonePicker";
import {
  EDGE_LINK,
  EDGE_TYPES,
  edgeTypeLabel as typeLabel,
  resolveEdges,
} from "@/lib/edgeProfiles";
import { siteImg, usePageHead } from "@/lib/siteContent";

const viewSeg = (on) =>
  `h-9 px-3.5 text-xs font-semibold transition-colors duration-200 ${
    on ? "bg-[#1c1b19] text-white" : "text-[#1c1b19]/70 hover:text-[#1c1b19]"
  }`;

export default function SiteEdges() {
  const site = useSite();
  const fallback = useEdgeStone();
  const [params, setParams] = useSearchParams();
  const type = EDGE_TYPES.some(([k]) => k === params.get("type"))
    ? params.get("type")
    : "all";
  const [open, setOpen] = useState(null);
  const [view, setView] = useState("section");
  const edges = useMemo(() => resolveEdges(site?.config?.edges), [site]);
  // { profile, picker, vs } — vs = compare partner when a shared link asked for it
  const [full, setFull] = useState(null);
  const [linkApplied, setLinkApplied] = useState(false);
  useEffect(() => {
    if (linkApplied || !edges.length) return;
    const p = edges.find((e) => e.slug === params.get("edge"));
    if (p) {
      setFull({ profile: p, picker: false, vs: edges.find((e) => e.slug === params.get("vs")) || null });
    }
    setLinkApplied(true);
  }, [edges, params, linkApplied]);
  const [picked, setPicked] = useState(null); // a slab off the racks, or null for the default
  const stone = picked || fallback;
  const wantStone = params.get("stone") || "";
  const stones = useEdgeStones(Boolean(open || full || wantStone));
  const [stoneApplied, setStoneApplied] = useState(false);
  useEffect(() => {
    if (!wantStone || stoneApplied || !stones) return;
    setPicked(stones.find((s) => s.key === wantStone) || null);
    setStoneApplied(true);
  }, [wantStone, stones, stoneApplied]);
  const shareLink = (p, s, b) =>
    `${window.location.origin}/collections/material/edges?edge=${p.slug}${
      s.key !== "default" ? `&stone=${encodeURIComponent(s.key)}` : ""
    }${b ? `&vs=${b.slug}` : ""}`;

  usePageHead({
    title: "Edge profiles",
    description:
      "Countertop edge profiles cut and polished in-house: eased, bevel, bullnose, ogee, cove ogee, triple pencil, chiseled and laminated 6 cm edges.",
  });

  useEffect(() => {
    if (!open || full) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, full]);

  const visible = useMemo(
    () => edges.filter((e) => type === "all" || e.type === type),
    [edges, type],
  );
  const groups = useMemo(() => {
    const out = [];
    for (const [key, label] of EDGE_TYPES.slice(1)) {
      const list = visible.filter((e) => e.type === key);
      if (list.length) out.push([key, label, list]);
    }
    return out;
  }, [visible]);

  const pick = (key) => {
    const next = new URLSearchParams(params);
    if (key === "all") next.delete("type");
    else next.set("type", key);
    setParams(next, { replace: true });
  };

  return (
    <SiteShell site={site} testId="site-edges">
      <main className="px-6 pb-20 pt-8 sm:pb-28 sm:pt-12 lg:px-12">
        <div
          className="s-warm px-6 py-8 sm:px-10 sm:py-12 lg:px-14 lg:py-14"
          data-testid="edges-hero"
        >
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="s-rise max-w-3xl">
              <h1
                className="mt-5 text-4xl font-semibold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
                data-testid="edges-headline"
              >
                Edges
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-[color:var(--s-mute)] sm:mt-6 sm:text-lg">
                The profile along the front of the counter is the detail you
                touch every day. Every edge below is cut and polished in-house
                on our CNC in any of our stones — single thickness or laminated
                to a chunky 6 cm.
              </p>
            </div>
            <div
              className="flex flex-wrap items-center gap-2"
              data-testid="edges-filter"
            >
              {EDGE_TYPES.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`edges-filter-${key}`}
                  onClick={() => pick(key)}
                  className={`s-pill !h-11 !text-sm transition-colors duration-200 ${
                    type === key ? "s-pill-dark" : "hover:!bg-[color:var(--s-surface)]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {groups.map(([key, label, list]) => (
          <section
            key={key}
            className="mt-10"
            data-testid={`edges-group-${key}`}
          >
            <div className="flex items-end justify-between gap-4 px-1">
              <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                {label} countertops
              </h2>
              <span className="text-sm text-[color:var(--s-mute)]">
                {list.length} profiles
              </span>
            </div>
            <div className="s-warm mt-5 p-4 sm:p-6">
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {list.map((e, n) => (
                  <article
                    key={e.slug}
                    data-testid={`edge-card-${e.slug}`}
                    className="s-rise s-card group flex flex-col"
                    style={{ animationDelay: `${Math.min(n, 9) * 50}ms` }}
                  >
                    <button
                      type="button"
                      data-testid={`edge-view-${e.slug}`}
                      onClick={() => setOpen(e)}
                      className="s-card-fig relative block aspect-[4/3] w-full cursor-zoom-in overflow-hidden"
                    >
                      {e.photo ? (
                        <img src={siteImg(e.photo)} alt={`${e.name} edge`} className="h-full w-full object-cover" data-testid={`edge-photo-${e.slug}`} />
                      ) : (
                        <EdgeSection profile={e} className="h-full w-full" />
                      )}
                      <span className="absolute bottom-3 right-3 flex translate-y-2 items-center gap-1.5 rounded-full bg-white/92 px-3.5 py-2 text-xs font-medium text-[color:var(--s-ink)] opacity-0 backdrop-blur transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                        <MagnifyingGlassPlus size={12} weight="bold" /> Quick
                        view
                      </span>
                      <span className="absolute left-3 top-3">
                        <span className="s-pill !h-8 !px-3 !text-xs">
                          {typeLabel(e.type)}
                        </span>
                      </span>
                    </button>
                    <div className="mt-4 flex items-end justify-between gap-3 px-1 pb-1">
                      <div className="min-w-0">
                        <h3 className="truncate text-xl font-semibold tracking-tight">
                          {e.name}
                        </h3>
                        <p className="mt-0.5 truncate text-sm text-[color:var(--s-mute)]">
                          {e.tag}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          data-testid={`edge-3d-${e.slug}`}
                          onClick={() => setFull({ profile: e, picker: false })}
                          className="s-btn s-sm s-no-arrow !h-12 !min-h-0 !gap-1.5 !px-4"
                          title={`See the ${e.name} edge in 3D`}
                        >
                          <Cube size={15} weight="bold" /> 3D
                        </button>
                        <button
                          type="button"
                          data-testid={`edge-more-${e.slug}`}
                          onClick={() => setOpen(e)}
                          className="s-icon-btn !h-12 !w-12"
                          title="Read more"
                        >
                          <ArrowRight size={18} />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        ))}
      </main>

      {open && (
        <div
          className="site fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center sm:p-10"
          data-testid="edge-dialog"
          onClick={() => setOpen(null)}
          role="presentation"
        >
          <div
            className="s-card s-still relative grid w-full max-w-4xl gap-6 !p-5 sm:!p-8 lg:grid-cols-[1.1fr_1fr]"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <button
              type="button"
              data-testid="edge-dialog-close"
              onClick={() => setOpen(null)}
              className="s-icon-btn absolute right-4 top-4 !h-10 !w-10 !rounded-xl"
              aria-label="Close"
            >
              <X size={16} weight="bold" />
            </button>
            <div className="flex flex-col gap-3">
              <div
                className="flex items-center gap-1 self-start bg-[color:var(--s-cream)] p-1"
                data-testid="edge-dialog-view"
              >
                <button
                  type="button"
                  data-testid="edge-dialog-view-section"
                  aria-pressed={view === "section"}
                  onClick={() => setView("section")}
                  className={viewSeg(view === "section")}
                >
                  Section
                </button>
                <button
                  type="button"
                  data-testid="edge-dialog-view-3d"
                  aria-pressed={view === "3d"}
                  onClick={() => setView("3d")}
                  className={`${viewSeg(view === "3d")} flex items-center gap-1.5`}
                >
                  <Cube size={13} weight="bold" /> 3D · {stone.name}
                </button>
              </div>
              <div className="s-card-fig aspect-[4/3] overflow-hidden">
                {view === "3d" ? (
                  <Edge3DInline
                    profile={open}
                    stone={stone}
                    onExpand={(picker) => setFull({ profile: open, picker })}
                  />
                ) : open.photo ? (
                  <img src={siteImg(open.photo)} alt={`${open.name} edge`} className="h-full w-full object-cover" data-testid="edge-dialog-photo" />
                ) : (
                  <EdgeSection profile={open} className="h-full w-full" large />
                )}
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <span className="s-pill !h-8 !w-fit !bg-[color:var(--s-cream)] !px-3 !text-xs">
                {typeLabel(open.type)}
              </span>
              <h2
                className="mt-4 text-3xl font-semibold tracking-tight"
                data-testid="edge-dialog-title"
              >
                {open.name}
              </h2>
              <p className="mt-1 text-base text-[color:var(--s-mute)]">
                {open.tag}
              </p>
              <p className="mt-5 text-sm leading-relaxed text-[color:var(--s-ink)]">
                {open.body}
              </p>
              <div className="s-chip mt-5 !items-start !px-4 !py-3">
                <span className="s-chip-k !justify-start">Best for</span>
                <span className="s-chip-v !whitespace-normal text-sm leading-snug">
                  {open.best}
                </span>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={EDGE_LINK(open.name)}
                  className="s-btn"
                  data-testid="edge-dialog-quote"
                >
                  Quote this edge
                </Link>
                <Link to="/inventory" className="s-ghost">
                  Pick a stone
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {full && (
        <Edge3DModal
          profile={full.profile}
          stone={stone}
          fallback={fallback}
          stones={stones}
          onStone={setPicked}
          pickerOpen={full.picker}
          initialB={full.vs}
          initialCompare={Boolean(full.vs)}
          shareLink={shareLink}
          onClose={() => setFull(null)}
        />
      )}
    </SiteShell>
  );
}
